import { Stack, type Theme, useMediaQuery } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAllCatalogProducts } from '/@/hooks/catalog';
import { useMyFields } from '/@/hooks/fields';
import { useUserLocations } from '/@/hooks/user';
import { getEnableRvAdvisorOnboarding, getSessionCountry, getUser } from '/@/store/session/selectors';
import type { Field } from '/@/types/field';
import ActivitySection from '../components/activity-selection';
import CreateRecommendationModal from '../components/create-recommendation-modal';
import FieldSection from '../components/field-section';
import LocationsSelectorModal from '../components/locations-selector-modal';
import ProductsSection from '../components/product-section';
import SuggestionsSection from '../components/suggestions-section';
import YieldEstimationModal from '../components/yield-estimation-modal';
import 'driver.js/dist/driver.css';
import { useSearchParams } from 'react-router-dom';
import PageContainer from '/@/components/bayer/page-container';
import EmailVerificationAlert from '/@/components/email-verification-alert';
import settings from '/@/core/settings';
import { useRecommendationsDashboard } from '/@/hooks/dashboards';
import { useHumidityMaps } from '/@/hooks/maps';
import { useOwnUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { getNodeScopeFilters } from '/@/utils/node-scope';
import Header from '../../../../components/bayer/header';
import { OnboardingModalAndAlert } from '../components/onboarding-modal-and-alert';
import UserUIAlertAndModal from '../components/onboarding-modal-and-alert/user-ui-alert-and-modal';
import LocationsSelector from '../components/zone-selector';
import { useRvHomeHelpTour } from './useRvHomeHelpTour';

export const ONBOARING_IDS = {
    FIRST: 'field-card',
    SECOND: 'product-explorer',
    THIRD: 'recommendation-guide',
    FOURTH: 'recommendation-create',
    FIFTH: 'maps-section',
};

const getDriverSteps = (hasHumidityMaps: boolean) => {
    const steps = [
        {
            element: `#${ONBOARING_IDS.FIRST}`,
            popover: {
                title: _('Productores'),
                description: _(
                    'Aquí podés ver y gestionar tus productores. Empezá creando un nuevo productor o importando desde FieldView.'
                ),
            },
            enabled: true,
        },
        {
            element: `#${ONBOARING_IDS.SECOND}`,
            popover: {
                title: _('Explorador de productos'),
                description: _(
                    'Buscá y filtrá productos por cultivo, necesidad o zona. Accedé fácilmente a sus características, ventajas, beneficios y recomendaciones.'
                ),
            },
            enabled: true,
        },
        {
            element: `#${ONBOARING_IDS.THIRD}`,
            popover: {
                title: _('Guía de recomendaciones'),
                description: _(
                    'Mensajes personalizados que se actualizan con el calendario agronómico. Te guían sobre qué acción tomar y cuándo.'
                ),
            },
            enabled: true,
        },
        {
            element: `#${ONBOARING_IDS.FOURTH}`,
            popover: {
                title: _('Crear recomendación'),
                description: _(
                    'Generá recomendaciones para los lotes de tus productores con respaldo técnico y comercial. Es el núcleo de la herramienta {{appName}}.',
                    { appName: settings.app.name }
                ),
            },
            enabled: false,
        },
        {
            element: `#${ONBOARING_IDS.FIFTH}`,
            popover: {
                title: _('Mapas y datos satelitales'),
                description: _(
                    'Visualizá humedad del suelo, heladas, GDUs y más para tomar decisiones basadas en datos.'
                ),
            },
            enabled: hasHumidityMaps,
        },
    ];
    return steps.filter((step) => step.enabled);
};

const RVHome = () => {
    // Feature Flags
    const enableRvAdvisorOnboarding = useSelector(getEnableRvAdvisorOnboarding);

    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [openLocationSelectorModal, setOpenLocationSelectorModal] = useState(false);
    const [openCreateRecommendationModal, setOpenCreateRecommendationModal] = useState(false);
    const [openYieldEstimationModal, setOpenYieldEstimationModal] = useState(false);
    const [selectedField, setSelectedField] = useState<Field | undefined>(undefined);
    const [driverActive, setDriverActive] = useState(false);

    const user = useSelector(getUser);
    const userNodeQuery = useOwnUserNode();
    const userNodeId = userNodeQuery.data?.id;
    const country = useSelector(getSessionCountry);
    const userLocationsQuery = useUserLocations({ level: 2, pageSize: 100000 }, { enabled: !!country });

    // Queries that affect the positioning of elements
    const fieldsQuery = useMyFields(
        {
            page: 1,
            pageSize: 10000,
            country: country?.code || '',
            selectOnly: 'id,lat,lng,name,location,altitude,area,geometry,externalProvider',
            userNodeId,
            includeChildren: true,
            excludeTargetNode: true,
        },
        { enabled: !!country?.code && !!userNodeId }
    );

    const recommendationDashboardQuery = useRecommendationsDashboard(
        {
            node: userNodeId,
            excludeTargetNode: true,
        },
        {
            enabled: !!userNodeId,
        }
    );

    const cornBrand = useSelector((state: any) =>
        state.catalog.brands?.data?.results?.find((b: any) => b.type === 'corn')
    );
    const cornProductsQuery = useAllCatalogProducts(
        {
            country: country?.code || '',
            brand: cornBrand?.name,
            crop: 'corn',
        },
        { enabled: !!country?.code && !!cornBrand?.name }
    );
    // Commented to priorize RV-Advisor modal onboarding and linking
    // useEffect(() => {
    //     if (!userLocationsQuery.data) return;
    //     if (userLocationsQuery.data.results.length === 0) {
    //         setOpenLocationSelectorModal(true);
    //     }
    // }, [userLocationsQuery.data]);

    const hidelocationsSelector = country?.code !== 'AR';

    const humidityMapsQuery = useHumidityMaps({ country: country.code });

    const driverSteps = useMemo(() => getDriverSteps(!!humidityMapsQuery.data?.length), [humidityMapsQuery.data]);

    const [searchParams] = useSearchParams();
    const isNotificationIconOpen = searchParams.get('notificationOpen') === 'true';

    const isDriverLoading =
        userLocationsQuery.isValidating ||
        fieldsQuery.isValidating ||
        recommendationDashboardQuery.isValidating ||
        cornProductsQuery.isValidating ||
        openLocationSelectorModal ||
        humidityMapsQuery.isValidating ||
        isNotificationIconOpen;

    const { startHelpTourFromIcon } = useRvHomeHelpTour(driverSteps, !isDriverLoading);

    return (
        <PageContainer
            boxProps={{ sx: { background: '#F5F5F5' } }}
            header={
                <Header
                    title={
                        <>
                            {_('¡Hola, ')}
                            <span className='mp-mask'>{user?.first_name}</span>
                        </>
                    }
                    subtitle={_('Potenciá las decisiones de tus productores y el rendimiento de sus campos.')}
                    actions={
                        hidelocationsSelector
                            ? []
                            : [
                                  <LocationsSelector
                                      key={'locations-selector'}
                                      onClick={() => setOpenLocationSelectorModal(true)}
                                  />,
                              ]
                    }
                />
            }
            boxChildren={
                <>
                    {settings.app.theme === 'expertobayer' && <EmailVerificationAlert />}
                    <LocationsSelectorModal
                        // TODO: Remove this once we have implemented filtering by country
                        open={openLocationSelectorModal && !hidelocationsSelector}
                        setOpen={setOpenLocationSelectorModal}
                    />
                    <CreateRecommendationModal
                        open={openCreateRecommendationModal}
                        setOpen={setOpenCreateRecommendationModal}
                        selectedField={selectedField}
                        setSelectedField={setSelectedField}
                        entrySource='rv-home'
                    />
                    <YieldEstimationModal open={openYieldEstimationModal} setOpen={setOpenYieldEstimationModal} />
                </>
            }
        >
            <Stack my={3}>
                {enableRvAdvisorOnboarding && <OnboardingModalAndAlert />}
                <UserUIAlertAndModal />
            </Stack>
            <Stack className='mp-mask' spacing={4}>
                <Grid2 spacing={3} container mx={{ sm: '-12px !important' }} alignItems='stretch'>
                    <Grid2 xs={12} sm={5} md={6} lg={4} sx={[isSmOrLower && { px: 0 }, { display: 'flex' }]}>
                        <ActivitySection
                            setOpenCreateRecommendationModal={setOpenCreateRecommendationModal}
                            onGrowersHelpClick={startHelpTourFromIcon}
                        />
                    </Grid2>
                    <Grid2
                        xs={12}
                        sm={7}
                        md={6}
                        lg={4}
                        sx={[isSmOrLower && { px: 0 }, { display: 'flex' }]}
                        id={ONBOARING_IDS.SECOND}
                    >
                        <ProductsSection setOpenLocationSelectorModal={setOpenLocationSelectorModal} />
                    </Grid2>
                    <Grid2
                        xs={12}
                        sm={12}
                        md={12}
                        lg={4}
                        sx={[isSmOrLower && { px: 0 }, { display: 'flex' }]}
                        id={ONBOARING_IDS.THIRD}
                    >
                        <SuggestionsSection setOpenYieldEstimationModal={setOpenYieldEstimationModal} />
                    </Grid2>
                </Grid2>
                <FieldSection
                    userNodeId={userNodeId}
                    title={_('Lotes de mis productores')}
                    subtitle={_('Buscá un lote por nombre y generá una recomendación rápida y precisa.')}
                    nodeScope={getNodeScopeFilters('growers')}
                />
            </Stack>
        </PageContainer>
    );
};

export default RVHome;
